'use client'

import { UserWithMentor } from '@/types/mentor'
import {
  Card,
  Text,
  Stack,
  Title,
  Button,
} from '@mantine/core'
import Link from 'next/link'
import { Avatar } from './Avatar'
import classes from './MentorCard.module.css'
import { ExpertiseBadgeGroup, UserBadgeGroup } from './badges'
import { useMentorSearchStore } from '@/stores/useMentorSearchStore'

interface MentorCardProps {
  user: UserWithMentor;
  priority?: boolean; // For LCP optimization on first card
}

export default function MentorCard({ user, priority = false }: MentorCardProps) {
  const { mentor } = user

  const handleClick = (e: React.MouseEvent) => {
    // Capture scroll BEFORE navigation
    if (typeof window !== 'undefined') {
      const scrollY = window.scrollY;
      const url = window.location.pathname + window.location.search;
      sessionStorage.setItem('mentorSearchScrollY', scrollY.toString());
      sessionStorage.setItem('mentorSearchUrl', url);
      sessionStorage.setItem('cameFromMentorSearch', 'true');
    }
  };

  return (
    <Link 
      href={`/mentors/${mentor?.handle ?? ''}`} 
      style={{ textDecoration: 'none' }}
      prefetch={false}
      data-mentor-handle={mentor?.handle}
      onClick={handleClick}
    >
      <Card
        padding="lg"
        radius="md"
        className={`mentor-card ${classes.mentorCard}`}
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%', 
          cursor: 'pointer', 
          backgroundColor: 'white',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden'
        }}
      >
        {/* Avatar + basic info */}
        <Stack align="center" gap="sm" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Avatar 
            radius={100} 
            size={140} 
            userId={user.id} 
            avatarId={user.avatarId} 
            className={classes.mentorAvatar}
            loading={priority ? "eager" : "lazy"}
            priority={priority}
          />

          <Stack gap={6} align="center" style={{ textAlign: 'center', width: '100%', flex: '0 0 auto' }}>
            <Title order={2}>
              {user.name}
            </Title>
            {mentor?.professionalTagline && (
              <Text
                size="sm"
                c="dimmed"
                fw={500}
                fs="italic"
                style={{
                  marginTop: '-0.35rem',
                  maxWidth: '85%',
                  lineHeight: 1.45,
                  textWrap: 'balance',
                  hyphens: 'auto',
                }}
              >
                {mentor.professionalTagline}
              </Text>
            )}
            <Text
              size="md"
              c="dimmed"
              className={classes.bioText}
              style={{
                // Clamp to 5 lines to keep cards equal height
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                marginTop: '0.5rem',
                marginBottom: '1rem',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                width: '100%',
                minHeight: '8rem',
                lineHeight: 1.6,
                textWrap: 'pretty',
                hyphens: 'auto',
              }}
            >
              {mentor?.bio || '\u00A0'}
            </Text>
          </Stack>

          {/* Expertise badges */}
          <div className={classes.badgeSection} style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 'auto' }}>
            <ExpertiseBadgeGroup expertises={mentor?.expertises ?? []} />
          </div>

          {/* User badges */}
          <div className={classes.userBadgeSection} style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '0.5rem' }}>
            <UserBadgeGroup badges={(user as any).badges ?? []} />
          </div>
        </Stack>

        <Button variant="light" color="navy" fullWidth mt="md" className={classes.viewProfileButton}>
          View Profile
        </Button>
      </Card>
    </Link>
  )
}